import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Tv, Monitor, Smartphone, Layout, Settings, Cpu, Zap, Info, ShieldCheck, Search } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia IPTV para Google TV", href: "/blog/guia-iptv-google-tv" }
  ];

  const faq = [
    {
      question: "Qual a diferença entre Android TV e Google TV?",
      answer: "O Google TV é uma evolução visual e funcional do Android TV, focada em recomendações personalizadas e uma interface muito mais fluida e moderna."
    },
    {
      question: "O Google TV aceita aplicativos de IPTV?",
      answer: "Sim, todos os aplicativos compatíveis com Android TV funcionam perfeitamente no Google TV, com o benefício de uma integração melhor com a busca por voz do sistema."
    },
    {
      question: "Como instalo o app P2 PRO no Chromecast com Google TV?",
      answer: "O processo é similar ao Android TV: você usa o app Downloader para baixar nosso APK exclusivo e habilita as permissões de desenvolvedor no menu de sistema."
    }
  ];

  const benefits = [
    {
      title: "Interface de Última Geração",
      description: "Navegue pelo IPTV dentro de um ecossistema visual premiado e extremamente rápido.",
      icon: <Layout className="w-6 h-6" />
    },
    {
      title: "Recomendações Inteligentes",
      description: "O sistema Google TV aprende seus gostos e facilita a descoberta de novos filmes e séries no nosso catálogo VOD.",
      icon: <Search className="w-6 h-6" />
    },
    {
      title: "Integração Total Google Home",
      description: "Controle sua TV pelo celular ou por comandos de voz em qualquer lugar da casa.",
      icon: <Settings className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Ative o Modo Desenvolvedor",
      description: "Clique 7 vezes na versão do sistema para liberar as configurações extras."
    },
    {
      title: "Downloader App",
      description: "Instale o Downloader e insira o código do nosso aplicativo exclusivo."
    },
    {
      title: "Sincronização P2 PRO",
      description: "Faça login e aguarde a indexação inteligente da nossa grade de canais."
    },
    {
      title: "Aperte o Play",
      description: "Curta o melhor do entretenimento 4K com a fluidez que só o Google TV oferece."
    }
  ];

  const diferenciais = [
    {
      title: "Suporte Dolby Vision/Atmos",
      description: "Transmissões otimizadas para os sistemas de som e imagem premium dos novos Chromecasts.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Perfil de Usuário",
      description: "Crie perfis diferentes para cada membro da família dentro do nosso app no Google TV.",
      icon: <Info className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Google TV: A Nova Fronteira do Streaming de Elite",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O <strong>Google TV</strong> representa o que há de mais moderno em sistemas operacionais para televisores. Presente nos novos Chromecasts e em TVs topo de linha de marcas como Sony e TCL, ele oferece uma experiência focada na descoberta de conteúdo. Quando integrado à lista de altíssima fidelidade da <strong>P2 PRO</strong>, o Google TV se torna a ferramenta definitiva para quem não quer perder tempo procurando o que assistir.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia avançado, vamos explorar como tirar o melhor proveito da inteligência do Google TV aplicada ao IPTV. Vamos ensinar como instalar o nosso ecossistema de apps, como usar a busca universal para encontrar canais em tempo real e como configurar seu dispositivo para que ele entregue 100% do bitrate que nossos servidores fornecem. Se você busca o auge da sofisticação tecnológica, este é o seu guia.
          </p>
        </div>
      )
    },
    {
      title: "2. Chromecast 4K com Google TV: O Hardware Perfeito",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Zap className="w-6 h-6 text-electric" /> Processamento HDR Dedicado</h4>
              <p className="text-sm text-muted-foreground">O hardware do Google TV faz o mapeamento de cores dinâmico, garantindo que nossos canais 4K tenham o melhor contraste possível.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Smartphone className="w-6 h-6 text-amber-500" /> Controle Remoto Minimalista</h4>
              <p className="text-sm text-muted-foreground">Otimizamos nosso app para que todas as funções importantes (EPG, Favoritos) estejam a um clique de distância no controle do Google.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo: Instalando o App P2 PRO",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">O Google TV esconde algumas opções para sua segurança. Veja como liberar:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Liberação do Sistema</h4>
              <p className="text-muted-foreground">Vá em Configurações {">"} Sistema {">"} Sobre {">"} Edição do Android TV. Clique repetidamente até ver a mensagem 'Você agora é um desenvolvedor'.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Instalação via Código</h4>
              <p className="text-sm text-muted-foreground">No app Downloader, insira o código curto da P2 PRO. O sistema baixará e instalará o player otimizado automaticamente.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas de Otimização para Google TV",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Mantenha sua experiência fluida com estes ajustes profissionais:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Priorize o Wi-Fi de 5GHz</h5>
                <p className="text-sm text-muted-foreground">O Google TV exige uma conexão estável para as animações da interface. Use sempre a banda de 5GHz para o streaming de vídeo não ser afetado.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Ajuste de Buffer Adaptativo</h5>
                <p className="text-sm text-muted-foreground">Nas configurações do nosso app, habilite o 'Smart Buffer'. Ele usará a RAM extra do Google TV para garantir que o canal nunca trave.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Suporte via QR Code</h5>
                <p className="text-sm text-muted-foreground">Se precisar de ajuda, nosso app no Google TV gera um QR Code que te leva direto para o nosso suporte humano no WhatsApp.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias Dispositivos"
        title="Guia IPTV para Google TV: O Manual de Elite 2026"
        subtitle="A sofisticação do Google encontra a potência da P2 PRO. Aprenda a configurar seu dispositivo para a melhor experiência visual."
        heroImage="https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "O Google TV não é apenas um sistema operacional; é um assistente pessoal de entretenimento. Presente nos Chromecasts de 4ª geração e nas TVs mais modernas do mercado, ele oferece uma fluidez invejável. Para quem busca um IPTV que 'simplesmente funciona' com beleza e rapidez, o Google TV é a plataforma ideal, e a P2 PRO é o combustível perfeito para ela.",
          "Neste guia técnico, vamos desbravar as camadas de inteligência do Google TV. Você aprenderá como configurar seu ambiente para suportar 4K nativo, como instalar nossos aplicativos exclusivos e como garantir que sua rede esteja preparada para o alto tráfego de dados que um streaming de elite exige.",
          "Prepare-se para uma aula de modernidade. No final desta leitura, seu Google TV estará configurado como um setup profissional, pronto para te entregar milhares de conteúdos com a estabilidade que é marca registrada da P2 PRO."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🌟 Pronto para a Melhor Imagem do Brasil?"
        ctaDescription="A P2 PRO é otimizada para os codecs avançados do Google TV. Faça seu teste gratuito e comprove a diferença."
        ctaButtonText="Solicitar Teste para Google TV"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-iptv-google-tv")({
  head: () => ({
    meta: [
      { title: "Guia IPTV para Google TV: Configuração e Performance 2026" },
      { name: "description", content: "Passo a passo completo para IPTV no Google TV. Aprenda a instalar o app P2 PRO, configurar o Chromecast e otimizar sua imagem 4K HDR." },
    ],
  }),
  component: ArticlePage,
});
